import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";

import { useAIAgentThread } from "@/AgentThread/AgentThreadSidebar/AIAgentThreadContext";
import { useCurrentOrgId } from "@/providers/auth/hooks";
import { ThreadVisibilityControl } from "@/shared/components/agentThread/ThreadVisibilityControl";
import { RobotoIconButton } from "@/shared/components/RobotoIconButton";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { type AgentThreadRecord } from "@/shared/domain/agentThread";
import { useActiveAgentThread } from "@/shared/domain/agentThread/hooks";

import styles from "./AgentThreadSidebarControls.module.css";
import { EarlyAccessInfoPopover } from "./EarlyAccessInfoPopover.tsx";

interface ActiveThreadVisibilityControlProps {
  thread: AgentThreadRecord | undefined;

  /** Whether the body has been swapped for the thread history list. */
  showHistory: boolean;
}

const ActiveThreadVisibilityControl = ({
  thread,
  showHistory,
}: ActiveThreadVisibilityControlProps) => {
  // Hidden rather than disabled on the new-thread view: a greyed-out lock
  // would read as "this thread is private" when there is no thread yet.
  if (thread === undefined) {
    return null;
  }

  // Opening history leaves `currentThreadId` set, so this control would go on
  // offering share and un-share for a thread that is no longer on screen —
  // one click from re-scoping the wrong thread, next to a list of the others.
  if (showHistory) {
    return null;
  }

  return <ThreadVisibilityControl thread={thread} variant="icon" />;
};

interface AgentThreadSidebarControlsProps {
  onClose: () => void;
}

export const AgentThreadSidebarControls = ({
  onClose,
}: AgentThreadSidebarControlsProps) => {
  const orgId = useCurrentOrgId();
  const {
    currentThreadId,
    isFullscreen,
    handleNewThread,
    handleToggleFullscreen,
    handleToggleHistory,
    showHistory,
  } = useAIAgentThread();

  // `loadMessages` is part of the query key, so `true` reuses the cache entry
  // the sidebar's AgentThread mounts rather than fetching the thread twice.
  // This header reads only the thread's visibility.
  const { data: activeThread } = useActiveAgentThread(currentThreadId, {
    orgId,
    loadMessages: true,
  });

  return (
    <header className={styles.header}>
      <div className={styles.titleContainer}>
        <EarlyAccessInfoPopover />
      </div>
      <div className={styles.navItems}>
        <ActiveThreadVisibilityControl
          thread={activeThread}
          showHistory={showHistory}
        />
        <RobotoTooltip
          title={showHistory ? "Hide My Threads" : "Show My Threads"}
        >
          <span>
            <RobotoIconButton
              onClick={handleToggleHistory}
              eventName={"AgentThreadHistoryToggled"}
              size="small"
            >
              {showHistory ? (
                <LibraryBooksIcon className={styles.activeIconButton} />
              ) : (
                <LibraryBooksIcon className={styles.iconButton} />
              )}
            </RobotoIconButton>
          </span>
        </RobotoTooltip>
        <RobotoTooltip title={"New Thread"}>
          <span>
            <RobotoIconButton
              onClick={handleNewThread}
              eventName={"NewAgentThreadClicked"}
              size="small"
            >
              <AddIcon className={styles.iconButton} />
            </RobotoIconButton>
          </span>
        </RobotoTooltip>
        <RobotoTooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
          <span>
            <RobotoIconButton
              onClick={handleToggleFullscreen}
              eventName={"AgentThreadFullscreenToggled"}
              size="small"
            >
              {isFullscreen ? (
                <FullscreenExitIcon className={styles.iconButton} />
              ) : (
                <FullscreenIcon className={styles.iconButton} />
              )}
            </RobotoIconButton>
          </span>
        </RobotoTooltip>
        <RobotoTooltip title={"Close"}>
          <span>
            <RobotoIconButton
              onClick={onClose}
              eventName={"CloseAgentThreadWindowClose"}
              size="small"
            >
              <CloseIcon className={styles.iconButton} />
            </RobotoIconButton>
          </span>
        </RobotoTooltip>
      </div>
    </header>
  );
};
